import React, { useState, useEffect, useCallback, useContext, createContext, ReactNode } from 'react';

export type Environment = 'local' | 'stage' | 'prod';

const ENVIRONMENT_KEY = 'admin_environment';
const DEFAULT_ENVIRONMENT: Environment = 'local';

// Custom event name for environment changes within the same tab
const ENVIRONMENT_CHANGE_EVENT = 'environment-change';

interface EnvironmentContextType {
  environment: Environment;
  setEnvironment: (env: Environment) => void;
}

const EnvironmentContext = createContext<EnvironmentContextType | null>(null);

/**
 * Provider component for environment state.
 * Wrap your app with this to enable environment switching.
 */
export const EnvironmentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [environment, setEnvironmentState] = useState<Environment>(() => {
    const stored = localStorage.getItem(ENVIRONMENT_KEY);
    if (stored === 'local' || stored === 'stage' || stored === 'prod') {
      return stored;
    }
    return DEFAULT_ENVIRONMENT;
  });

  // Sync with localStorage and dispatch custom event
  const setEnvironment = useCallback((env: Environment) => {
    if (env !== 'local' && env !== 'stage' && env !== 'prod') {
      console.error(`Invalid environment: ${env}`);
      return;
    }
    localStorage.setItem(ENVIRONMENT_KEY, env);
    setEnvironmentState(env);
    
    // Dispatch custom event for other components in the same tab
    window.dispatchEvent(new CustomEvent(ENVIRONMENT_CHANGE_EVENT, { detail: env }));
  }, []);

  // Listen for changes from other tabs (storage event)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === ENVIRONMENT_KEY && e.newValue) {
        if (e.newValue === 'local' || e.newValue === 'stage' || e.newValue === 'prod') {
          setEnvironmentState(e.newValue);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return (
    <EnvironmentContext.Provider value={{ environment, setEnvironment }}>
      {children}
    </EnvironmentContext.Provider>
  );
};

/**
 * Hook for managing the current admin environment (stage/prod).
 * Must be used within an EnvironmentProvider.
 * 
 * Usage:
 * ```tsx
 * const [environment, setEnvironment] = useEnvironment();
 * ```
 */
export const useEnvironment = (): [Environment, (env: Environment) => void] => {
  const context = useContext(EnvironmentContext);
  
  // Fallback for components outside the provider (maintains backward compatibility)
  const [fallbackEnvironment, setFallbackEnvironmentState] = useState<Environment>(() => {
    const stored = localStorage.getItem(ENVIRONMENT_KEY);
    if (stored === 'local' || stored === 'stage' || stored === 'prod') {
      return stored;
    }
    return DEFAULT_ENVIRONMENT;
  });

  // Listen for custom event when outside provider
  useEffect(() => {
    if (context) return; // Skip if using context
    
    const handleEnvironmentChange = (e: Event) => {
      const customEvent = e as CustomEvent<Environment>;
      if (customEvent.detail === 'local' || customEvent.detail === 'stage' || customEvent.detail === 'prod') {
        setFallbackEnvironmentState(customEvent.detail);
      }
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === ENVIRONMENT_KEY && e.newValue) {
        if (e.newValue === 'local' || e.newValue === 'stage' || e.newValue === 'prod') {
          setFallbackEnvironmentState(e.newValue);
        }
      }
    };

    window.addEventListener(ENVIRONMENT_CHANGE_EVENT, handleEnvironmentChange);
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener(ENVIRONMENT_CHANGE_EVENT, handleEnvironmentChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [context]);

  const setFallbackEnvironment = useCallback((env: Environment) => {
    if (env !== 'local' && env !== 'stage' && env !== 'prod') {
      console.error(`Invalid environment: ${env}`);
      return;
    }
    localStorage.setItem(ENVIRONMENT_KEY, env);
    setFallbackEnvironmentState(env);
    window.dispatchEvent(new CustomEvent(ENVIRONMENT_CHANGE_EVENT, { detail: env }));
  }, []);

  if (context) {
    return [context.environment, context.setEnvironment];
  }

  return [fallbackEnvironment, setFallbackEnvironment];
};

/**
 * Get the current environment from localStorage.
 * Use this in non-React contexts (e.g., API calls).
 */
export const getCurrentEnvironment = (): Environment => {
  const stored = localStorage.getItem(ENVIRONMENT_KEY);
  if (stored === 'local' || stored === 'stage' || stored === 'prod') {
    return stored;
  }
  return DEFAULT_ENVIRONMENT;
};

/**
 * Get environment display info for UI.
 */
export const getEnvironmentInfo = (env: Environment) => {
  return {
    local: {
      label: 'Local',
      color: 'bg-blue-500',
      textColor: 'text-blue-500',
      description: 'Local development database',
    },
    stage: {
      label: 'Stage',
      color: 'bg-amber-500',
      textColor: 'text-amber-500',
      description: 'Development/testing environment',
    },
    prod: {
      label: 'Production',
      color: 'bg-red-500',
      textColor: 'text-red-500',
      description: 'Live production data',
    },
  }[env];
};

export default useEnvironment;
